import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Filter, Calendar, Tag, FileText, Globe2, Building2, Lightbulb, BarChart3 } from 'lucide-react';

const Reports = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortOrder, setSortOrder] = useState('newest');

  // Report categories
  const categories = [
    {
      name: 'Commodities',
      description: 'In-depth analysis of global energy commodities including oil, gas, coal, and critical minerals markets.',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
      icon: <BarChart3 className="w-8 h-8" />,
      count: 24
    },
    {
      name: 'Country',
      description: 'Comprehensive country-level assessments covering energy policy, market dynamics, and investment climate.',
      image: 'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=800&q=80',
      icon: <Globe2 className="w-8 h-8" />,
      count: 45
    },
    {
      name: 'Region',
      description: 'Regional market intelligence spanning major energy corridors, trade routes, and economic zones.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
      icon: <Globe2 className="w-8 h-8" />,
      count: 18
    },
    {
      name: 'Company',
      description: 'Strategic company profiles, competitive analysis, and corporate intelligence across the energy sector.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
      icon: <Building2 className="w-8 h-8" />,
      count: 36
    },
    {
      name: 'Intelligence Reports',
      description: 'Specialized intelligence briefs on emerging trends, market disruptions, and strategic opportunities.',
      image: 'https://images.unsplash.com/photo-1748439281934-2803c6a3ee36?w=800&q=80',
      icon: <Lightbulb className="w-8 h-8" />,
      count: 15
    }
  ];

  // Sample reports data
  const reports = [
    {
      id: 1,
      title: 'Global Crude Oil Market Outlook 2025',
      category: 'Commodities',
      summary: 'Comprehensive analysis of global crude oil supply-demand dynamics, price forecasts, and market structure through 2025.',
      date: '2024-12-15',
      price: '$2,500',
      featured: true
    },
    {
      id: 2,
      title: 'Nigeria Energy Sector Deep Dive',
      category: 'Country',
      summary: 'In-depth assessment of Nigeria\'s oil & gas industry, regulatory environment, and investment opportunities.',
      date: '2024-12-10',
      price: '$1,800',
      featured: true
    },
    {
      id: 3,
      title: 'West Africa LNG Export Infrastructure',
      category: 'Region',
      summary: 'Strategic analysis of LNG export capacity, pipeline networks, and infrastructure developments across West Africa.',
      date: '2024-12-08',
      price: '$2,200',
      featured: false
    },
    {
      id: 4,
      title: 'Critical Minerals Supply Chain Analysis',
      category: 'Commodities',
      summary: 'Detailed examination of lithium, cobalt, and rare earth supply chains with producer profiles and trade flows.',
      date: '2024-12-05',
      price: '$3,000',
      featured: true
    },
    {
      id: 5,
      title: 'Saudi Aramco Strategic Assessment',
      category: 'Company',
      summary: 'Comprehensive analysis of Saudi Aramco\'s operations, strategy, financials, and competitive positioning.',
      date: '2024-12-01',
      price: '$1,500',
      featured: false
    },
    {
      id: 6,
      title: 'Energy Transition Investment Trends',
      category: 'Intelligence Reports',
      summary: 'Intelligence brief on capital flows, investment patterns, and emerging opportunities in the energy transition.',
      date: '2024-11-28',
      price: '$2,000',
      featured: true
    },
    {
      id: 7,
      title: 'European Natural Gas Security',
      category: 'Region',
      summary: 'Assessment of European gas supply security, storage dynamics, and policy implications post-2024.',
      date: '2024-11-25',
      price: '$2,400',
      featured: false
    },
    {
      id: 8,
      title: 'Shell Plc Competitive Analysis',
      category: 'Company',
      summary: 'Strategic review of Shell\'s portfolio transformation, low-carbon investments, and market positioning.',
      date: '2024-11-20',
      price: '$1,500',
      featured: false
    },
    {
      id: 9,
      title: 'Global Coal Market Outlook',
      category: 'Commodities',
      summary: 'Analysis of thermal and metallurgical coal markets, trade flows, and price dynamics through 2026.',
      date: '2024-11-15',
      price: '$2,200',
      featured: false
    },
    {
      id: 10,
      title: 'UAE Energy Policy Review',
      category: 'Country',
      summary: 'Comprehensive review of UAE\'s energy strategy, ADNOC operations, and renewable energy ambitions.',
      date: '2024-11-10',
      price: '$1,800',
      featured: false
    },
    {
      id: 11,
      title: 'Carbon Markets Intelligence Brief',
      category: 'Intelligence Reports',
      summary: 'Deep dive into global carbon markets, pricing mechanisms, and regulatory developments.',
      date: '2024-11-05',
      price: '$1,600',
      featured: false
    },
    {
      id: 12,
      title: 'Kazakhstan Oil & Gas Assessment',
      category: 'Country',
      summary: 'Strategic assessment of Kazakhstan\'s upstream sector, export routes, and investment climate.',
      date: '2024-11-01',
      price: '$1,800',
      featured: false
    }
  ];

  // Filter reports based on search and category
  const filteredReports = reports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          report.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || report.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    if (sortOrder === 'newest') {
      return new Date(b.date) - new Date(a.date);
    } else {
      return new Date(a.date) - new Date(b.date);
    }
  });

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Reports</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Institutional-grade research and intelligence across global energy and resources markets, connecting data, expertise, and analytics to deliver deeper insight into the evolving global energy system.
            </p>
          </div>
        </div>
      </section>

      {/* Report Categories Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">Report Categories</h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Explore our comprehensive library of research across key sectors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category.name)}
                className={`bg-[#F8F9FA] rounded-lg border overflow-hidden text-left transition-all duration-300 group hover:shadow-lg hover:-translate-y-1 ${
                  selectedCategory === category.name ? 'border-[#007BFF] shadow-lg' : 'border-slate-200 hover:border-[#007BFF]'
                }`}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-xl font-bold text-white">
                    {category.name}
                  </h3>
                  <span className="absolute top-4 right-4 px-3 py-1 bg-white/90 text-[#0F2A4D] text-sm font-medium rounded-full">
                    {category.count} Reports
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-slate-600 text-base leading-relaxed mb-4">
                    {category.description}
                  </p>
                  <span className="inline-flex items-center text-[#007BFF] font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Explore Reports
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Reports Listing Section */}
      <section id="reports-listing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">Available Reports</h2>
            <p className="text-base lg:text-lg text-slate-600">
              Browse and filter our complete report library
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-10">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Search Bar */}
              <div className="flex-grow">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-slate-500" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF] bg-white min-w-[180px]"
                >
                  <option value="All">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Sort Order */}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-500" />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF] bg-white min-w-[140px]"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-slate-600">
              Showing <span className="font-semibold text-[#1F3C88]">{sortedReports.length}</span> reports
              {selectedCategory !== 'All' && <span> in <span className="font-semibold">{selectedCategory}</span></span>}
            </p>
          </div>

          {/* Reports Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedReports.map((report) => (
              <div
                key={report.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex-grow">
                  {/* Category Tag */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center px-3 py-1 bg-[#007BFF]/10 text-[#007BFF] text-xs font-semibold rounded-full">
                      <Tag className="w-3 h-3 mr-1" />
                      {report.category}
                    </span>
                    {report.featured && (
                      <span className="px-2 py-1 bg-[#CFA300]/10 text-[#CFA300] text-xs font-semibold rounded">
                        Featured
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-[#1F3C88] mb-3 line-clamp-2">
                    {report.title}
                  </h3>

                  {/* Summary */}
                  <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">
                    {report.summary}
                  </p>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-sm text-slate-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(report.date)}
                    </div>
                    <div className="text-lg font-bold text-[#1F3C88]">
                      {report.price}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Link
                      to="/contact"
                      className="flex-1 inline-flex items-center justify-center py-2.5 bg-[#007BFF] text-white text-sm font-semibold rounded-md hover:bg-[#0066DD] transition-colors"
                    >
                      View Report
                    </Link>
                    <Link
                      to="/contact"
                      className="flex-1 inline-flex items-center justify-center py-2.5 bg-[#CFA300] text-white text-sm font-semibold rounded-md hover:bg-[#B8920A] transition-colors"
                    >
                      Buy Report
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {sortedReports.length === 0 && (
            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No reports found</h3>
              <p className="text-slate-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Custom Research?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our team can provide tailored intelligence briefs and bespoke research to meet your specific requirements.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
          >
            Request Custom Research
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Reports;
